import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Coins, Save, Plus, Edit2, Trash2, X, Eye, EyeOff, CheckCircle2, XCircle, RefreshCw, Star, ExternalLink, Settings as SettingsIcon, Package, Receipt, Users as UsersIcon, ArrowRight, Sparkles, BarChart3, Upload } from 'lucide-react';
import CustomerProfileDrawer from './CustomerProfileDrawer';
import EVCAnalytics from './EVCAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EVCEditor — admin panel for the EVC reseller integration. Four sub-tabs:
 *   • Settings    — API credentials + welcome HTML.
 *   • Packages    — 7 admin-editable bundles (CRUD + auto savings badge).
 *   • Purchases   — full purchase log (clickable customer link → drawer).
 *   • Customers   — aggregated view: who linked EVC IDs, how much they
 *                   spent, current EVC balance, last purchase.
 */
export default function EVCEditor({ token }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [tab, setTab] = useState('settings');
  const [openCustomerId, setOpenCustomerId] = useState(null);

  // Centralised loaders so each sub-tab can refresh its own slice.
  const [settings, setSettings] = useState(null);
  const [packages, setPackages] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  const loadSettings = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/evc/settings`, { headers });
    if (r.ok) setSettings(await r.json());
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPackages = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/evc/packages`, { headers });
    if (r.ok) setPackages((await r.json()).items || []);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPurchases = useCallback(async () => {
    setLoadingPurchases(true);
    try {
      const r = await fetch(`${API_URL}/api/evc/purchases?limit=200`, { headers });
      if (r.ok) setPurchases((await r.json()).items || []);
    } finally { setLoadingPurchases(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const r = await fetch(`${API_URL}/api/evc/admin/customers`, { headers });
      if (r.ok) setCustomers((await r.json()).items || []);
    } finally { setLoadingCustomers(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadSettings(); loadPackages(); loadPurchases(); loadCustomers(); }, [loadSettings, loadPackages, loadPurchases, loadCustomers]);

  const TABS = [
    { id: 'settings',  label: 'Settings',  Icon: SettingsIcon },
    { id: 'packages',  label: 'Packages',  Icon: Package, count: packages.length },
    { id: 'purchases', label: 'Purchases', Icon: Receipt,  count: purchases.length },
    { id: 'customers', label: 'Customers', Icon: UsersIcon, count: customers.length },
    { id: 'analytics', label: 'Analytics', Icon: BarChart3 },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="evc-admin-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-lg">
          <Coins className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">EVC WinOLS Credits</h1>
          <p className="text-sm text-slate-500">Admin · API credentials, packages, purchase log and customer roster</p>
        </div>
      </div>

      {/* Sub-tab strip */}
      <div className="flex flex-wrap gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm" data-testid="evc-subtabs">
        {TABS.map(({ id, label, Icon, count }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`evc-subtab-${id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${active ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <Icon className="w-4 h-4" /> {label}
              {count !== undefined && count > 0 && (
                <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${active ? 'bg-white/25' : 'bg-slate-100 text-slate-500'}`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'settings'  && <SettingsTab token={token} settings={settings} setSettings={setSettings} reload={loadSettings} />}
      {tab === 'packages'  && <PackagesTab token={token} packages={packages} reload={loadPackages} />}
      {tab === 'purchases' && <PurchasesTab purchases={purchases} loading={loadingPurchases} reload={loadPurchases} onOpenCustomer={setOpenCustomerId} />}
      {tab === 'customers' && <CustomersTab customers={customers} loading={loadingCustomers} reload={loadCustomers} onOpenCustomer={setOpenCustomerId} token={token} setCustomers={setCustomers} />}
      {tab === 'analytics' && <EVCAnalytics token={token} />}

      {openCustomerId && (
        <CustomerProfileDrawer
          token={token} customerId={openCustomerId}
          onClose={() => setOpenCustomerId(null)}
          onSaved={() => { loadCustomers(); loadPurchases(); }}
        />
      )}
    </div>
  );
}


/* -----------------------------------------------------------------------
 * Settings tab
 * --------------------------------------------------------------------- */
function SettingsTab({ token, settings, setSettings, reload }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [savingSettings, setSavingSettings] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = { ...settings };
      if (payload.password === '***' || payload.password === '') delete payload.password;
      const r = await fetch(`${API_URL}/api/evc/settings`, {
        method: 'PUT', headers, body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert('Save failed: ' + (err.detail || r.status));
      } else {
        setSettings(await r.json());
        alert('Settings saved.');
        reload();
      }
    } finally { setSavingSettings(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/evc/admin/test`, { method: 'POST', headers });
      setTestResult(await r.json().catch(() => ({})));
    } catch (e) { setTestResult({ ok: false, message: e.message }); }
    finally { setTesting(false); }
  };

  if (!settings) return <div className="p-8 text-center text-slate-500" data-testid="evc-loading">Loading EVC settings…</div>;

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4" data-testid="evc-settings-section">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-800">API Credentials & Welcome Text</h2>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} data-testid="evc-enabled-toggle" />
          <span className={settings.enabled ? 'text-emerald-700 font-medium' : 'text-slate-500'}>{settings.enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="API ID (apiid)">
          <input className="w-full border rounded-lg px-3 py-2 text-sm" value={settings.apiid || ''} data-testid="evc-apiid-input" onChange={(e) => setSettings({ ...settings, apiid: e.target.value })} />
        </Field>
        <Field label="Username">
          <input className="w-full border rounded-lg px-3 py-2 text-sm" value={settings.username || ''} data-testid="evc-username-input" onChange={(e) => setSettings({ ...settings, username: e.target.value })} />
        </Field>
        <Field label="API Password">
          <div className="relative">
            <input type={showPwd ? 'text' : 'password'} className="w-full border rounded-lg px-3 py-2 pr-10 text-sm" value={settings.password || ''} placeholder="(unchanged)" data-testid="evc-password-input" onChange={(e) => setSettings({ ...settings, password: e.target.value })} />
            <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700" data-testid="evc-password-toggle">
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>
        <Field label="Currency">
          <input className="w-full border rounded-lg px-3 py-2 text-sm" value={settings.currency || 'EUR'} data-testid="evc-currency-input" onChange={(e) => setSettings({ ...settings, currency: e.target.value })} />
        </Field>
      </div>

      <Field label="Welcome / intro text (HTML)">
        <textarea className="w-full border rounded-lg px-3 py-2 text-sm font-mono" rows={5} value={settings.intro_text || ''} data-testid="evc-intro-text" onChange={(e) => setSettings({ ...settings, intro_text: e.target.value })} />
      </Field>

      <Field label="Link-EVC-ID prompt (shown above the input field on the client page)">
        <textarea
          className="w-full border rounded-lg px-3 py-2 text-sm"
          rows={3}
          value={settings.link_text || ''}
          data-testid="evc-link-text"
          onChange={(e) => setSettings({ ...settings, link_text: e.target.value })}
          placeholder="e.g. Enter the customer ID we created for you on chiptuningprotools.com…"
        />
      </Field>

      {/* Secondary CTA — extra link that lets customers request EVC
          reseller confirmation directly. Rendered on the customer EVC
          page right under `link_text`. Three fields: enabled toggle,
          phrase, URL — admin can disable it entirely (e.g. when EVC
          confirmation goes through a different channel for a tenant). */}
      <Field label="Confirmation request link — visible">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={settings.confirmation_link_enabled !== false}
            data-testid="evc-confirmation-link-enabled"
            onChange={(e) => setSettings({ ...settings, confirmation_link_enabled: e.target.checked })}
          />
          <span>Show the confirmation link block on the customer EVC page</span>
        </label>
      </Field>

      <Field label="Confirmation link phrase (shown above the URL)">
        <input
          type="text"
          className="w-full border rounded-lg px-3 py-2 text-sm"
          value={settings.confirmation_link_text || ''}
          data-testid="evc-confirmation-link-text"
          onChange={(e) => setSettings({ ...settings, confirmation_link_text: e.target.value })}
          placeholder="e.g. Also additionally you can use this link to request confirmation:"
        />
      </Field>

      <Field label="Confirmation link URL (opens in a new tab)">
        <input
          type="url"
          className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
          value={settings.confirmation_link_url || ''}
          data-testid="evc-confirmation-link-url"
          onChange={(e) => setSettings({ ...settings, confirmation_link_url: e.target.value })}
          placeholder="https://www.evc.de/en/product/ols/reseller/reseller.asp?name=…"
        />
      </Field>

      <Field label="Help block (HTML — shown at the bottom of the client EVC page as 'How it works')">
        <textarea
          className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
          rows={10}
          value={settings.help_text || ''}
          data-testid="evc-help-text"
          onChange={(e) => setSettings({ ...settings, help_text: e.target.value })}
          placeholder="HTML allowed — e.g. <ul><li>...</li></ul>"
        />
      </Field>

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button onClick={saveSettings} disabled={savingSettings} className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50" data-testid="evc-save-settings-btn">
          <Save className="w-4 h-4" /> {savingSettings ? 'Saving…' : 'Save Settings'}
        </button>
        <button onClick={testConnection} disabled={testing} className="px-4 py-2 border border-slate-300 rounded-lg font-medium flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50" data-testid="evc-test-connection-btn">
          <RefreshCw className={`w-4 h-4 ${testing ? 'animate-spin' : ''}`} /> {testing ? 'Testing…' : 'Test Connection'}
        </button>
        {testResult && (
          <span className={`text-sm flex items-center gap-1.5 ${testResult.ok ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="evc-test-result">
            {testResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {testResult.ok ? 'Connected' : (testResult.message || 'Failed')}
          </span>
        )}
      </div>

      <p className="text-xs text-slate-400 flex items-center gap-1.5 pt-2">
        Powered by EVC reseller API · <a href="https://services.evc.de/services/api_resellercredits2.asp" target="_blank" rel="noreferrer" className="underline hover:text-slate-600 flex items-center gap-0.5">docs <ExternalLink className="w-3 h-3" /></a>
      </p>
    </section>
  );
}


/* -----------------------------------------------------------------------
 * Packages tab — with auto savings badge
 * --------------------------------------------------------------------- */
function PackagesTab({ token, packages, reload }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [editingPkg, setEditingPkg] = useState(null);
  const [showAddPkg, setShowAddPkg] = useState(false);

  // Baseline = €/credit on the cheapest pack. Used to show a "Save €X" badge
  // on every other pack that's cheaper per credit than that baseline.
  const baselinePerCredit = useMemo(() => computeBaseline(packages), [packages]);

  const savePackage = async (pkg) => {
    const isNew = !pkg.id;
    const url = isNew ? `${API_URL}/api/evc/packages` : `${API_URL}/api/evc/packages/${pkg.id}`;
    const r = await fetch(url, {
      method: isNew ? 'POST' : 'PUT', headers,
      body: JSON.stringify({
        label: pkg.label, credits: parseInt(pkg.credits, 10) || 0,
        price_eur: parseFloat(pkg.price_eur) || 0, popular: !!pkg.popular,
        sort_order: parseInt(pkg.sort_order, 10) || 100,
        description: pkg.description || '',
      }),
    });
    if (!r.ok) { alert('Save failed: ' + ((await r.json().catch(() => ({}))).detail || r.status)); return; }
    setEditingPkg(null); setShowAddPkg(false); reload();
  };

  const deletePackage = async (id) => {
    if (!window.confirm('Delete this package? Customers will no longer see it.')) return;
    const r = await fetch(`${API_URL}/api/evc/packages/${id}`, { method: 'DELETE', headers });
    if (r.ok) reload();
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="evc-packages-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-semibold text-slate-800">Credit Packages</h2>
          <p className="text-xs text-slate-500 mt-0.5">Savings badge auto-computed against the cheapest pack ({baselinePerCredit ? `€${baselinePerCredit.toFixed(2)}/credit` : '—'}).</p>
        </div>
        <button onClick={() => setShowAddPkg(true)} className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg text-sm font-medium flex items-center gap-1.5" data-testid="evc-add-package-btn">
          <Plus className="w-4 h-4" /> Add package
        </button>
      </div>

      {packages.length === 0 ? (
        <div className="text-sm text-slate-500 py-4">No packages yet. Click <b>Add package</b> to create the first one.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {packages.map((p) => {
            const savings = computeSavings(p, baselinePerCredit);
            return (
              <div key={p.id} className={`relative border rounded-xl p-4 ${p.popular ? 'border-amber-400 bg-amber-50/40' : 'border-slate-200 bg-white'}`} data-testid={`evc-pkg-card-${p.id}`}>
                {p.popular && (
                  <div className="absolute -top-2.5 left-3 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" /> POPULAR
                  </div>
                )}
                {savings > 0 && (
                  <div className="absolute -top-2.5 right-3 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1" data-testid={`evc-pkg-save-${p.id}`}>
                    <Sparkles className="w-3 h-3" /> SAVE €{savings.toFixed(0)}
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-800 truncate">{p.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Sort #{p.sort_order}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setEditingPkg(p)} className="p-1.5 text-slate-500 hover:text-blue-600" data-testid={`evc-pkg-edit-${p.id}`}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => deletePackage(p.id)} className="p-1.5 text-slate-500 hover:text-rose-600" data-testid={`evc-pkg-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <div className="text-2xl font-extrabold text-slate-800">{p.credits}</div>
                  <div className="text-sm text-slate-500">credits</div>
                </div>
                <div className="mt-1 text-lg font-semibold text-amber-700">€{Number(p.price_eur).toFixed(2)}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">€{(Number(p.price_eur) / Math.max(p.credits, 1)).toFixed(2)} / credit</div>
                {p.description && <div className="mt-2 text-xs text-slate-500">{p.description}</div>}
              </div>
            );
          })}
        </div>
      )}

      {(editingPkg || showAddPkg) && (
        <PackageEditor
          initial={editingPkg || { label: '', credits: 10, price_eur: 100, popular: false, sort_order: (packages.length + 1) }}
          onCancel={() => { setEditingPkg(null); setShowAddPkg(false); }}
          onSave={savePackage}
        />
      )}
    </section>
  );
}


/* -----------------------------------------------------------------------
 * Purchases tab — with clickable account link
 * --------------------------------------------------------------------- */
function PurchasesTab({ purchases, loading, reload, onOpenCustomer }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="evc-purchases-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-slate-800">Purchase Log</h2>
        <button onClick={reload} disabled={loading} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50" data-testid="evc-refresh-purchases">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading && purchases.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading purchases…</div>
      ) : purchases.length === 0 ? (
        <div className="text-sm text-slate-500 py-4">No purchases yet.</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto -mx-2">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Account</th>
                  <th className="text-left px-3 py-2">EVC ID</th>
                  <th className="text-left px-3 py-2">Package</th>
                  <th className="text-right px-3 py-2">Credits</th>
                  <th className="text-right px-3 py-2">Price</th>
                  <th className="text-right px-3 py-2">Spent total</th>
                  <th className="text-right px-3 py-2" title="Live EVC balance snapshot recorded at the moment of this purchase. The customer may have spent credits since — see the Customers tab for the current live balance.">EVC bal. snapshot</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`evc-purchase-row-${p.id}`}>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{p.created_at?.slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => p.user_id && onOpenCustomer(p.user_id)}
                        disabled={!p.user_id}
                        className="text-blue-600 hover:underline disabled:text-slate-500 disabled:no-underline"
                        data-testid={`evc-purchase-account-${p.id}`}
                      >
                        {p.user_email}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono">#{p.evc_customer_id}</td>
                    <td className="px-3 py-2">{p.package_label}</td>
                    <td className="px-3 py-2 text-right font-semibold">{p.credits}</td>
                    <td className="px-3 py-2 text-right">€{Number(p.price_eur).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-slate-700 font-medium">€{Number(p.spent_to_date || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700" title="Live EVC balance at the moment of this top-up. Customer may have spent credits since.">{p.balance_after ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards — same data, no horizontal scroll */}
          <div className="md:hidden space-y-2.5" data-testid="evc-purchases-mobile">
            {purchases.map((p) => (
              <div
                key={`m-${p.id}`}
                className="border border-slate-200 rounded-xl p-3 bg-white shadow-sm"
                data-testid={`evc-purchase-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => p.user_id && onOpenCustomer(p.user_id)}
                      disabled={!p.user_id}
                      className="text-blue-600 hover:underline disabled:text-slate-700 disabled:no-underline text-sm font-semibold truncate text-left block max-w-full"
                    >
                      {p.user_email}
                    </button>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                      EVC #{p.evc_customer_id} · {p.created_at?.slice(0, 19).replace('T', ' ')}
                    </div>
                    <div className="text-[11px] text-slate-700 mt-0.5 truncate">{p.package_label}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-base font-bold text-amber-700">+{p.credits}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">credits</div>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
                  <div className="bg-slate-50 rounded-lg py-1">
                    <div className="text-[9px] uppercase text-slate-500">Price</div>
                    <div className="text-sm font-semibold text-slate-800">€{Number(p.price_eur).toFixed(2)}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg py-1">
                    <div className="text-[9px] uppercase text-slate-500">Spent total</div>
                    <div className="text-sm font-semibold text-slate-800">€{Number(p.spent_to_date || 0).toFixed(0)}</div>
                  </div>
                  <div className="bg-emerald-50 rounded-lg py-1">
                    <div className="text-[9px] uppercase text-emerald-700/70">EVC after</div>
                    <div className="text-sm font-semibold text-emerald-700">{p.balance_after ?? '—'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}


/* -----------------------------------------------------------------------
 * Customers tab — aggregated per-EVC-customer view
 * --------------------------------------------------------------------- */
function CustomersTab({ customers, loading, reload, onOpenCustomer, token, setCustomers }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | active | with-purchases | no-purchases
  // Default sort = highest live EVC balance first (admin's most common ask).
  const [sortKey, setSortKey] = useState('evc_balance');
  const [sortDir, setSortDir] = useState('desc');
  const [syncingId, setSyncingId] = useState(null);   // user_id currently re-polled
  const [bulkSyncing, setBulkSyncing] = useState(false);

  // Re-poll EVC for ONE customer. Mirrors the new backend endpoint
  // /api/evc/admin/sync-balance/{user_id} which both returns the fresh
  // value AND persists it on users.evc_balance so the customer profile
  // drawer and user's own EVC dashboard read the same number.
  const syncRow = async (userId) => {
    if (!userId) return;
    setSyncingId(userId);
    try {
      const r = await fetch(`${API_URL}/api/evc/admin/sync-balance/${userId}`, { method: 'POST', headers });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setCustomers((prev) => prev.map((c) => c.user_id === userId ? { ...c, evc_balance: j.evc_balance } : c));
    } catch (e) {
      // Lightweight failure surface — admin's preferred toast lib is `sonner`
      // but importing here pulls in unrelated UI; an alert keeps the patch
      // focused. The bulk button below uses sonner.
      alert(`EVC sync failed: ${e.message || e}`);
    } finally {
      setSyncingId(null);
    }
  };

  // Bulk-import of reseller names — opens the paste modal where admins
  // can copy rows straight from the EVC.de PDF/dashboard. Parse on the
  // client (tab- OR multi-space-separated), preview the count, then
  // POST to /api/evc/admin/import-reseller-names.
  const [importOpen, setImportOpen]   = useState(false);
  const [importText, setImportText]   = useState('');
  const [importOverwrite, setImportOverwrite] = useState(false);
  const [importBusy, setImportBusy]   = useState(false);
  const [importResult, setImportResult] = useState(null);

  const parsedPairs = useMemo(() => {
    if (!importText.trim()) return [];
    const out = [];
    importText.split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      // Accept tab-, comma-, or multi-space separator. Each row must
      // start with a numeric license account followed by the name.
      const m = trimmed.match(/^(\d{3,9})\s*[\t,;|]?\s*(.+)$/);
      if (!m) return;
      const account = parseInt(m[1], 10);
      const name = m[2].trim();
      if (!account || !name) return;
      out.push({ license_account: account, name });
    });
    return out;
  }, [importText]);

  const runImport = async () => {
    if (parsedPairs.length === 0) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const r = await fetch(`${API_URL}/api/evc/admin/import-reseller-names`, {
        method: 'POST', headers,
        body: JSON.stringify({ pairs: parsedPairs, overwrite: importOverwrite }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Import failed');
      setImportResult(j);
      // Refresh the list so the new names are visible immediately.
      await reload();
    } catch (e) {
      setImportResult({ error: e.message || String(e) });
    } finally {
      setImportBusy(false);
    }
  };

  const syncAll = async () => {
    setBulkSyncing(true);
    try {
      const r = await fetch(`${API_URL}/api/evc/admin/sync-balance-all`, { method: 'POST', headers });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      alert(`EVC sync complete — ${j.synced}/${j.total} synced, ${j.failed} failed. Refreshing list…`);
      await reload();
    } catch (e) {
      alert(`Bulk EVC sync failed: ${e.message || e}`);
    } finally {
      setBulkSyncing(false);
    }
  };

  const onSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('desc'); }
  };

  const filteredSorted = useMemo(() => {
    const s = search.trim().toLowerCase();
    let rows = customers.filter((c) => {
      if (s) {
        const hay = [
          c.user_email, c.first_name, c.surname, c.account_id,
          String(c.evc_customer_id || ''),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (filterMode === 'active'         && !(Number(c.evc_balance) > 0)) return false;
      if (filterMode === 'with-purchases' && !c.purchase_count) return false;
      if (filterMode === 'no-purchases'   &&  c.purchase_count)  return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case 'account':       av = `${a.first_name || ''} ${a.surname || ''} ${a.user_email || ''}`.toLowerCase(); bv = `${b.first_name || ''} ${b.surname || ''} ${b.user_email || ''}`.toLowerCase(); break;
        case 'reseller_name': av = (a.evc_reseller_name || '').toLowerCase(); bv = (b.evc_reseller_name || '').toLowerCase(); break;
        case 'evc_id':        av = Number(a.evc_customer_id) || 0; bv = Number(b.evc_customer_id) || 0; break;
        case 'purchases':     av = a.purchase_count || 0; bv = b.purchase_count || 0; break;
        case 'credits':       av = a.total_credits || 0; bv = b.total_credits || 0; break;
        case 'spent':         av = Number(a.total_spent) || 0; bv = Number(b.total_spent) || 0; break;
        case 'evc_balance':   av = a.evc_balance == null ? -1 : Number(a.evc_balance); bv = b.evc_balance == null ? -1 : Number(b.evc_balance); break;
        case 'last_purchase': av = a.last_purchase || ''; bv = b.last_purchase || ''; break;
        default:              av = 0; bv = 0;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    return rows;
  }, [customers, search, filterMode, sortKey, sortDir]);

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="evc-customers-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-semibold text-slate-800">EVC Customers</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Showing <b>{filteredSorted.length}</b> of {customers.length}. Click any column header to sort.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setImportOpen(true); setImportResult(null); }}
            className="px-3 py-1.5 border border-violet-300 bg-violet-50 text-violet-800 rounded-lg text-sm flex items-center gap-1.5 hover:bg-violet-100"
            title="Paste rows from the EVC.de PDF/dashboard to bulk-assign reseller names."
            data-testid="evc-import-reseller-names"
          >
            <Upload className="w-3.5 h-3.5" /> Import names
          </button>
          <button
            onClick={syncAll}
            disabled={bulkSyncing || loading}
            className="px-3 py-1.5 border border-amber-300 bg-amber-50 text-amber-800 rounded-lg text-sm flex items-center gap-1.5 hover:bg-amber-100 disabled:opacity-50"
            title="Re-poll EVC for every linked customer and persist the freshest balance."
            data-testid="evc-sync-all-balances"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${bulkSyncing ? 'animate-spin' : ''}`} /> {bulkSyncing ? 'Syncing…' : 'Sync all from EVC'}
          </button>
          <button onClick={reload} disabled={loading} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50" data-testid="evc-refresh-customers">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Search + filter bar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, account # or EVC ID…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
            data-testid="evc-customers-search"
          />
          <svg className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
        </div>
        <div className="inline-flex bg-slate-100 rounded-lg p-1 text-xs">
          {[
            { id: 'all',            label: `All (${customers.length})` },
            { id: 'active',         label: `Has balance` },
            { id: 'with-purchases', label: `With purchases` },
            { id: 'no-purchases',   label: `No purchases` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilterMode(f.id)}
              data-testid={`evc-filter-${f.id}`}
              className={`px-2.5 py-1 rounded font-bold transition-all ${
                filterMode === f.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >{f.label}</button>
          ))}
        </div>
        {(search || filterMode !== 'all') && (
          <button
            onClick={() => { setSearch(''); setFilterMode('all'); }}
            data-testid="evc-customers-reset"
            className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      {loading && customers.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Loading customers… (live EVC balances are fetched in parallel — usually 5–10 s)</div>
      ) : filteredSorted.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center" data-testid="evc-customers-empty">
          {customers.length === 0 ? 'No customers have linked an EVC ID yet.' : 'No customers match your filters.'}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto -mx-2">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <ThSort label="Account"       sortKey="account"       align="left"  cur={sortKey} dir={sortDir} onClick={onSort} />
                  <ThSort label="Reseller name" sortKey="reseller_name" align="left"  cur={sortKey} dir={sortDir} onClick={onSort} />
                  <ThSort label="EVC ID"        sortKey="evc_id"        align="left"  cur={sortKey} dir={sortDir} onClick={onSort} />
                  <ThSort label="Purchases"     sortKey="purchases"     align="right" cur={sortKey} dir={sortDir} onClick={onSort} />
                  <ThSort label="Total credits" sortKey="credits"       align="right" cur={sortKey} dir={sortDir} onClick={onSort} />
                  <ThSort label="Total spent"   sortKey="spent"         align="right" cur={sortKey} dir={sortDir} onClick={onSort} />
                  <ThSort label="EVC balance"   sortKey="evc_balance"   align="right" cur={sortKey} dir={sortDir} onClick={onSort} />
                  <th className="text-left px-3 py-2">30d activity</th>
                  <ThSort label="Last purchase" sortKey="last_purchase" align="left"  cur={sortKey} dir={sortDir} onClick={onSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredSorted.map((c) => (
                  <tr key={`${c.user_id}-${c.evc_customer_id}`} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`evc-customer-row-${c.user_id}-${c.evc_customer_id}`}>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => c.user_id && onOpenCustomer(c.user_id)}
                        disabled={!c.user_id}
                        className="text-blue-600 hover:underline disabled:text-slate-500 disabled:no-underline text-left"
                        data-testid={`evc-customer-link-${c.user_id}`}
                      >
                        <div className="font-medium">{[c.first_name, c.surname].filter(Boolean).join(' ') || c.user_email}</div>
                        <div className="text-xs text-slate-500">{c.user_email}{c.account_id ? ` · #${c.account_id}` : ''}</div>
                      </button>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700 max-w-[200px] truncate" title={c.evc_reseller_name || 'Add name in customer profile (EVC API does not expose it)'} data-testid={`evc-reseller-name-${c.user_id}`}>
                      {c.evc_reseller_name || <span className="text-slate-300 italic">— add in profile —</span>}
                    </td>
                    <td className="px-3 py-2 font-mono">#{c.evc_customer_id}</td>
                    <td className="px-3 py-2 text-right">{c.purchase_count}</td>
                    <td className="px-3 py-2 text-right font-semibold text-amber-700">{c.total_credits}</td>
                    <td className="px-3 py-2 text-right">€{Number(c.total_spent || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700 font-semibold">
                      <div className="inline-flex items-center gap-1">
                        <span data-testid={`evc-customer-balance-${c.user_id}`}>{c.evc_balance ?? '—'}</span>
                        <button
                          onClick={() => syncRow(c.user_id)}
                          disabled={syncingId === c.user_id || !c.user_id}
                          className="p-1 text-slate-400 hover:text-amber-600 disabled:opacity-50"
                          title="Re-poll EVC for this customer (updates everywhere)"
                          data-testid={`evc-sync-row-${c.user_id}`}
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${syncingId === c.user_id ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2"><Sparkline data={c.spark_30d || []} /></td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{c.last_purchase ? c.last_purchase.slice(0, 19).replace('T', ' ') : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => c.user_id && onOpenCustomer(c.user_id)}
                        className="p-1.5 text-slate-500 hover:text-blue-600"
                        title="Open customer profile"
                        data-testid={`evc-customer-open-${c.user_id}`}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards — same data, no horizontal scroll */}
          <div className="md:hidden space-y-2.5" data-testid="evc-customers-mobile">
            {filteredSorted.map((c) => (
              <button
                key={`m-${c.user_id}-${c.evc_customer_id}`}
                onClick={() => c.user_id && onOpenCustomer(c.user_id)}
                disabled={!c.user_id}
                className="w-full text-left bg-white border border-slate-200 rounded-xl p-3 shadow-sm hover:shadow active:bg-slate-50 transition disabled:opacity-60"
                data-testid={`evc-customer-card-${c.user_id}-${c.evc_customer_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 text-sm truncate">
                      {[c.first_name, c.surname].filter(Boolean).join(' ') || c.user_email}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {c.user_email}{c.account_id ? ` · #${c.account_id}` : ''}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">EVC #{c.evc_customer_id}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">EVC bal.</div>
                    <div className="text-base font-bold text-emerald-700">{c.evc_balance ?? '—'}</div>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
                  <div className="bg-slate-50 rounded-lg py-1">
                    <div className="text-[9px] uppercase text-slate-500">Purchases</div>
                    <div className="text-sm font-semibold text-slate-800">{c.purchase_count || 0}</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg py-1">
                    <div className="text-[9px] uppercase text-amber-700/70">Credits</div>
                    <div className="text-sm font-semibold text-amber-700">{c.total_credits || 0}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg py-1">
                    <div className="text-[9px] uppercase text-slate-500">Spent</div>
                    <div className="text-sm font-semibold text-slate-800">€{Number(c.total_spent || 0).toFixed(0)}</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase text-slate-500 mb-0.5">30d activity</div>
                    <Sparkline data={c.spark_30d || []} />
                  </div>
                  <div className="text-[10px] text-slate-500 text-right whitespace-nowrap">
                    <div className="opacity-70">Last buy</div>
                    <div>{c.last_purchase ? c.last_purchase.slice(0, 10) : '—'}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Import reseller names modal — admin pastes "license_account name"
          rows (tab/comma/space-separated). The PDF copy-paste from EVC.de
          produces tab-separated text which is parsed automatically. */}
      {importOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
          onClick={() => !importBusy && setImportOpen(false)}
          data-testid="evc-import-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><Upload className="w-4 h-4 text-violet-600" /> Import EVC reseller names</h3>
                <p className="text-xs text-slate-500 mt-0.5">Paste rows from your EVC.de credits accounts dashboard. Each row needs the License account number, then the Name — tab, comma, or space-separated.</p>
              </div>
              <button onClick={() => setImportOpen(false)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" disabled={importBusy}><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder={`22825\tJB Auto\n21367\tAutoffcina Cipolli & Baraldi\n240752\tEmmanuel Mella Pacheco\n…`}
                disabled={importBusy}
                rows={10}
                className="w-full font-mono text-xs px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50"
                data-testid="evc-import-textarea"
              />
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <div className="text-slate-600">
                  Parsed rows: <b data-testid="evc-import-parsed-count">{parsedPairs.length}</b>
                </div>
                <label className="inline-flex items-center gap-2 text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={importOverwrite}
                    onChange={(e) => setImportOverwrite(e.target.checked)}
                    disabled={importBusy}
                    data-testid="evc-import-overwrite"
                  />
                  Overwrite existing names
                </label>
              </div>

              {importResult && !importResult.error && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm" data-testid="evc-import-result">
                  <div className="font-bold text-emerald-800">
                    Import complete — {importResult.updated} updated, {importResult.kept_existing} kept, {importResult.no_matching_user} unmatched
                  </div>
                  {importResult.sample_updated?.length > 0 && (
                    <div className="mt-1 text-xs text-emerald-700/90">
                      Sample: {importResult.sample_updated.slice(0, 5).map((s) => `#${s.license_account} → ${s.name}`).join(' · ')}
                    </div>
                  )}
                </div>
              )}
              {importResult?.error && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-800" data-testid="evc-import-error">
                  {importResult.error}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                onClick={() => setImportOpen(false)}
                disabled={importBusy}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50"
                data-testid="evc-import-cancel"
              >
                Close
              </button>
              <button
                onClick={runImport}
                disabled={importBusy || parsedPairs.length === 0}
                className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
                data-testid="evc-import-submit"
              >
                {importBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {importBusy ? 'Importing…' : `Import ${parsedPairs.length} name${parsedPairs.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


/** Sortable column header. Renders an arrow when active. */
function ThSort({ label, sortKey, align = 'left', cur, dir, onClick }) {
  const active = cur === sortKey;
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`px-3 py-2 cursor-pointer select-none hover:text-slate-900 ${align === 'right' ? 'text-right' : 'text-left'} ${active ? 'text-amber-700' : ''}`}
      data-testid={`evc-customers-sort-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="opacity-50 text-[10px]">
          {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  );
}


/* -----------------------------------------------------------------------
 * Helpers + sub-components
 * --------------------------------------------------------------------- */

// Cheapest pack's €/credit ratio. Used as the savings baseline.
export function computeBaseline(packages) {
  if (!packages || packages.length === 0) return null;
  let cheapest = null;
  for (const p of packages) {
    if (!p.credits || !p.price_eur) continue;
    if (!cheapest || p.price_eur < cheapest.price_eur) cheapest = p;
  }
  if (!cheapest) return null;
  return Number(cheapest.price_eur) / Math.max(cheapest.credits, 1);
}

// € savings vs baseline price-per-credit. Returns 0 when ratio is the baseline.
export function computeSavings(pkg, baselinePerCredit) {
  if (!baselinePerCredit || !pkg.credits || !pkg.price_eur) return 0;
  const wouldCost = baselinePerCredit * pkg.credits;
  const savings = wouldCost - Number(pkg.price_eur);
  return savings > 0.5 ? savings : 0; // Hide sub-€1 noise.
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}

/**
 * Sparkline — tiny inline SVG showing a 30-day credit-purchase activity for
 * one customer. No deps, scales itself, returns a friendly em-dash placeholder
 * when the customer has zero activity in the window.
 */
function Sparkline({ data, width = 100, height = 24 }) {
  const arr = Array.isArray(data) ? data : [];
  const total = arr.reduce((a, b) => a + (Number(b) || 0), 0);
  if (!arr.length || total === 0) {
    return <span className="text-slate-300 text-xs" data-testid="evc-sparkline-empty">—</span>;
  }
  const max = Math.max(...arr.map((v) => Number(v) || 0), 1);
  const stepX = width / Math.max(arr.length - 1, 1);
  // Build the polyline points (top-down — invert Y so taller bars = recent buy).
  const pts = arr.map((v, i) => {
    const x = i * stepX;
    const y = height - ((Number(v) || 0) / max) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  // Filled area underneath
  const areaPts = `0,${height} ${pts} ${width},${height}`;
  return (
    <svg
      width={width} height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="block"
      data-testid="evc-sparkline"
      role="img"
      aria-label={`30-day purchase activity, total ${total} credits`}
    >
      <polygon points={areaPts} fill="#10b981" fillOpacity="0.15" />
      <polyline points={pts} fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {arr.map((v, i) => v > 0 && (
        <circle
          key={i}
          cx={i * stepX}
          cy={height - ((Number(v) || 0) / max) * (height - 2) - 1}
          r="1.5" fill="#059669"
        />
      ))}
    </svg>
  );
}

function PackageEditor({ initial, onCancel, onSave }) {
  const [draft, setDraft] = useState(initial);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="evc-package-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{initial.id ? 'Edit package' : 'Add package'}</h3>
          <button onClick={onCancel} className="p-1.5 text-slate-500 hover:text-slate-800"><X className="w-4 h-4" /></button>
        </div>
        <Field label="Label (e.g. '50 WinOLS Credits')">
          <input className="w-full border rounded-lg px-3 py-2 text-sm" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="evc-pkg-label" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Credits">
            <input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={draft.credits} onChange={(e) => setDraft({ ...draft, credits: e.target.value })} data-testid="evc-pkg-credits" />
          </Field>
          <Field label="Price (€)">
            <input type="number" step="0.01" className="w-full border rounded-lg px-3 py-2 text-sm" value={draft.price_eur} onChange={(e) => setDraft({ ...draft, price_eur: e.target.value })} data-testid="evc-pkg-price" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sort order">
            <input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} data-testid="evc-pkg-sort" />
          </Field>
          <label className="flex items-end gap-2 pb-2">
            <input type="checkbox" checked={!!draft.popular} onChange={(e) => setDraft({ ...draft, popular: e.target.checked })} data-testid="evc-pkg-popular" />
            <span className="text-sm">Mark as popular</span>
          </label>
        </div>
        <Field label="Description (optional)">
          <input className="w-full border rounded-lg px-3 py-2 text-sm" value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="evc-pkg-description" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
          <button onClick={() => onSave(draft)} disabled={!draft.label} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="evc-pkg-save">Save</button>
        </div>
      </div>
    </div>
  );
}
